/**
 * authenticate.jsx — Fonctions d'authentification utilisateur.
 *
 * Modif EarniConnect : la fonction `login` utilise désormais le flux SIWE complet :
 *   1. GET /auth/nonce?address=  → { nonce, message, expiresAt }
 *      (note : l'ancien AuthService.getNonce pointait sur /auth/nonce/:wallet)
 *   2. personal_sign(message, account) — on signe `message` (EIP-4361 complet)
 *   3. POST /auth/verify-signature { message, signature, address }
 *      → { user, accessToken }  OU  { action: 'REGISTER', address, verifiedAt }
 *
 * Si action === 'REGISTER' : l'adresse est inconnue du système → ouverture de la modal de
 * connexion en mode inscription (dispatch setWalletRegisterRequest, lu par header.jsx).
 * Le flow complet d'inscription est géré dans EarniWalletModal (WalletRegisterPanel).
 *
 * Comportement conservé (non cassé) :
 *   - Bascule de réseau wallet_switchEthereumChain
 *   - Stockage localStorage._user + _first
 *   - dispatch(setUser) / dispatch(setLoading)
 *   - Redirection _redirectAfterLogin
 *   - logout() inchangé
 *
 * ATTENTION : AuthService.getNonce utilise encore /auth/nonce/:wallet (ancien format).
 * On n'utilise PAS AuthService.getNonce ici — on appelle l'endpoint réel directement.
 * TODO : aligner AuthService.getNonce sur /auth/nonce?address= dans une tâche dédiée.
 */
import { EPATHS }                 from '../../routes/paths.enum';
import { axiosWithoutEncryption } from './apiCallWithToken';
import { fetchSiweNonce }         from './siweNonce';
import { signout }                from '../../services/AuthService';
import { resetCustodialListingCache } from './custodialListing';
import { setWalletRegisterRequest } from '../../store/actions';
import { SERVER_URL as serverURL } from '../../core/config';
import { writeStoredUser, removeStoredUser } from '../auth/userStorage';


/**
 * getNonceSIWE — GET /auth/nonce?address=<wallet> (dédupliqué, cf. siweNonce.js).
 * Retourne { nonce, message, expiresAt } où `message` est le challenge EIP-4361 complet.
 * On signe `message` tel quel (pas juste `nonce`).
 *
 * Délègue à fetchSiweNonce (cache module-level) : si le handler global accountsChanged ET une
 * instance EarniWalletModal demandent un nonce pour le même wallet quasi-simultanément, ils
 * partagent LE MÊME nonce → un seul upsert serveur → aucun écrasement → verify-signature réussit.
 *
 * @param {string} address — adresse wallet (0x...)
 * @returns {Promise<{nonce: string, message: string, expiresAt: string}>}
 */
async function getNonceSIWE(address) {
  return fetchSiweNonce(serverURL, address);
}

/**
 * verifySignatureSIWE — POST /auth/verify-signature
 * Retourne soit { user, accessToken } soit { action: 'REGISTER', address, verifiedAt }
 *
 * @param {{ message: string, signature: string, address: string }} payload
 * @returns {Promise<object>}
 */
async function verifySignatureSIWE(payload) {
  const result = await axiosWithoutEncryption(
    `${serverURL}/auth/verify-signature`,
    'post',
    payload
  );
  return result;
}

/**
 * login — Flow SIWE complet depuis window.ethereum.
 *
 * Utilisé par wallet.jsx (fallback si EarniWalletModal n'est pas disponible)
 * et par les autres points d'entrée legacy (ex: re-auth automatique).
 *
 * @param {function} dispatch
 * @param {function} setLoading   — action Redux setLoading
 * @param {function} setUser      — action Redux setUser
 * @param {object}   history      — react-router history
 * @param {function} [setServerError] — setter d'état local pour ModalNoMetamask
 * @returns {Promise<object|null>}
 */
export const login = async (dispatch, setLoading, setUser, history, setServerError = null) => {
  if (!window.ethereum) {
    if (setServerError) setServerError(true);
    return null;
  }

  dispatch(setLoading(true));

  /* Bascule de réseau si nécessaire */
  const CHAINID = Number(process.env.REACT_APP_AVAX_CHAIN_ID).toString();
  if (window.ethereum.networkVersion && window.ethereum.networkVersion !== CHAINID) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: process.env.REACT_APP_AVAX_CHAIN_ID }],
      });
    } catch (err) {
      /* Échec silencieux — l'utilisateur peut ignorer le changement de réseau */
    }
    dispatch(setLoading(false));
  }

  dispatch(setLoading(true));

  let result = {};

  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });

    if (accounts.length !== 0) {
      const account = accounts[0];

      /* 1. Obtenir le challenge EIP-4361 ──────────────────────────────────── */
      const { message } = await getNonceSIWE(account);

      /* 2. Signer le message tel quel (EIP-4361 complet) ──────────────────── */
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, account],
      });

      /* 3. Vérifier la signature ──────────────────────────────────────────── */
      const response = await verifySignatureSIWE({ message, signature, address: account });

      if (response?.action === 'REGISTER') {
        /*
         * Adresse inconnue → rediriger vers /register.
         * Le flow complet d'inscription est géré par EarniWalletModal
         * (WalletRegisterPanel) qui sera ouvert depuis wallet.jsx.
         */
        dispatch(setLoading(false));
        dispatch(setWalletRegisterRequest(true)); // ouvre la modal en inscription (ex-/register)
        return null;
      }

      /* Connexion réussie */
      const user = response;
      writeStoredUser(user);
      if (!user.epicUserId && !user.epicUsername) {
        localStorage.setItem('_first', String(true));
      }
      dispatch(setUser(user));
      dispatch(setLoading(false));
      result = user;

      /* Redirection _redirectAfterLogin si SessionTimeoutModal l'a posé */
      try {
        const returnTo = localStorage.getItem('_redirectAfterLogin');
        if (returnTo) {
          localStorage.removeItem('_redirectAfterLogin');
          history.push(returnTo);
        }
      } catch (e) { /* silencieux */ }

      return result;
    }
  } catch (error) {
    /*
     * Gestion des erreurs rétrocompatible avec l'ancien comportement :
     * - 404 JSON (compte inconnu) → redirection /register
     * - autres → log silencieux
     */
    try {
      const err = typeof error.message === 'string' ? JSON.parse(error.message) : {};
      if (err.status === 404) {
        dispatch(setLoading(false));
        dispatch(setWalletRegisterRequest(true)); // ouvre la modal en inscription (ex-/register)
        return null;
      }
    } catch { /* silencieux */ }
    console.log('login error:', error);
  }

  dispatch(setLoading(false));
  return result;
};


/**
 * logout — Déconnexion (inchangé).
 *
 * @param {function} dispatch
 * @param {function} setUser
 * @param {object}   history
 */
export const logout = async (dispatch, setUser, history, skipRedirect = false) => {
  try { await signout(); } catch (_) { /* silencieux */ }
  // La capacite custodialListing depend du wallet primaire de la session, invalider au logout
  // (la prochaine session re-resoudra a la demande). CHEMIN 1 (gasless custodial listing, SPEC v3).
  resetCustodialListingCache();
  removeStoredUser();
  localStorage.removeItem('_first');
  localStorage.removeItem('_authTs');
  dispatch(setUser({}));
  if (!skipRedirect) {
    history.push({ pathname: EPATHS.HOME });
  }
};
